export interface ParsedContent {
  mainText: string
  id?: string
  title?: string
  imageUrl?: string
  author?: string
  appLink?: string
}

export const parsePostContent = (rawContent: string): ParsedContent => {
  if (!rawContent) {
    return { mainText: '' }
  }

  // Initialize result
  const result: ParsedContent = {
    mainText: '',
    id: '',
    title: '',
    imageUrl: '',
    author: '',
    appLink: ''
  }

  try {
    // Extract ID pattern: <_id>658f1ef0d5a6727d5990cf2c
    const idMatch = rawContent.match(/<_id>([a-f0-9]+)/i)
    if (idMatch) {
      result.id = idMatch[1]
      // Remove the ID from content
      rawContent = rawContent.replace(/<_id>[a-f0-9]+/i, '')
    }

    // Extract CloudFront image URL
    const imageUrlMatch = rawContent.match(/(https:\/\/d3lu5mthgm0uw9\.cloudfront\.net\/[^\s]+)/i)
    if (imageUrlMatch) {
      result.imageUrl = imageUrlMatch[1]
      // Remove the image URL from content
      rawContent = rawContent.replace(/(https:\/\/d3lu5mthgm0uw9\.cloudfront\.net\/[^\s]+)/i, '')
    }

    // Extract app link (munroe.app.link)
    const appLinkMatch = rawContent.match(/(https:\/\/munroe\.app\.link\/[^\s]+)/i)
    if (appLinkMatch) {
      result.appLink = appLinkMatch[1]
      // Remove the app link from content
      rawContent = rawContent.replace(/(https:\/\/munroe\.app\.link\/[^\s]+)/i, '')
    }

    // Extract author (Dr. Myles Munroe pattern)
    const authorMatch = rawContent.match(/(Dr\.\s*Myles\s*Munroe)/i)
    if (authorMatch) {
      result.author = authorMatch[1]
      // Remove the author from content
      rawContent = rawContent.replace(/(Dr\.\s*Myles\s*Munroe)/i, '')
    }

    // Extract title - look for capitalized text that might be a title
    // Common patterns: "How To Test False Doctrines" etc.
    const titleMatch = rawContent.match(/([A-Z][a-zA-Z\s]+(?:Doctrines?|Teaching?s?|Message?s?|Sermon?s?|Study|Studies))/i)
    if (titleMatch) {
      result.title = titleMatch[1].trim()
      // Remove the title from content
      rawContent = rawContent.replace(titleMatch[0], '')
    }

    // Clean up the remaining content as main text
    result.mainText = rawContent
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/^\s+|\s+$/g, '') // Trim leading/trailing spaces
      .replace(/#+\w+/g, '') // Remove hashtags like #stayblessed
      .trim()

  } catch (error) {
    console.error('Error parsing content:', error)
    result.mainText = rawContent
  }

  return result
}

export const formatParsedContent = (parsed: ParsedContent): string => {
  let formatted = ''

  // Add main text
  if (parsed.mainText) {
    formatted += `<div class="mb-3">${parsed.mainText}</div>`
  }

  // Add title if available
  if (parsed.title) {
    formatted += `<div class="mb-2"><strong>📖 Content: </strong>${parsed.title}</div>`
  }

  // Add author if available
  if (parsed.author) {
    formatted += `<div class="mb-2"><strong>👨‍🏫 Author: </strong>${parsed.author}</div>`
  }

  // Add image if available
  if (parsed.imageUrl) {
    formatted += `
      <div class="mb-3">
        <strong>🖼️ Attached Image:</strong><br/>
        <img src="${parsed.imageUrl}" alt="Content Image" style="max-width: 300px; max-height: 200px; border-radius: 8px; margin-top: 8px;" class="img-fluid"/>
      </div>
    `
  }

  // Add app link if available
  if (parsed.appLink) {
    formatted += `
      <div class="mb-2">
        <strong>🔗 App Link: </strong>
        <a href="${parsed.appLink}" target="_blank" class="text-primary">Open in Munroe App</a>
      </div>
    `
  }

  // Add ID if available (for admin purposes)
  if (parsed.id) {
    formatted += `<div class="text-muted small mt-2"><strong>Content ID:</strong> ${parsed.id}</div>`
  }

  return formatted || '<div class="text-muted">No content available</div>'
}